/**
 * Programar el envío para más tarde.
 *
 * El backend es la fuente de verdad de la fecha (vive en Mongo, no en un timer): acá sólo se elige
 * el momento y se muestra qué va a pasar. Los "atajos" (mañana 10hs, etc.) son los momentos
 * razonables reales — nadie manda una promo a las 3am, y si lo intenta, la franja horaria lo frena.
 */
import { useEffect, useState } from "react";
import { CalendarClock, Loader2, X, AlertTriangle, Check } from "lucide-react";
import { activaApi } from "../api";

interface Props {
  campaignId: string;
  /**
   * Grupos a programar, con la convención de la API: undefined = todos, lista no vacía =
   * subconjunto. Una lista vacía NO es «todos»: es un error del que llama y no se programa.
   */
  grupos?: string[];
  /** el operador destildó todos los grupos: no hay a quién programarle nada */
  sinGrupos?: boolean;
  /** líneas de WhatsApp elegidas ([] o ausente = todas las conectadas) */
  lineas?: string[];
  /** para que la página refresque el estado tras programar/cancelar */
  onCambio?: () => void;
  /** si ya está programada, la fecha que devolvió el backend */
  programadoPara?: string | null;
  /** el scheduler la encontró demasiado tarde y no la mandó sola: la decide una persona */
  programacionVencida?: boolean;
}

/** "2026-07-16T10:00" (lo que pide datetime-local) a partir de un Date */
function paraInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function atajo(diasDesdeHoy: number, hora: number): string {
  const d = new Date();
  d.setDate(d.getDate() + diasDesdeHoy);
  d.setHours(hora, 0, 0, 0);
  return paraInput(d);
}

const ATAJOS = [
  { label: "Mañana 10hs", valor: () => atajo(1, 10) },
  { label: "Mañana 19hs", valor: () => atajo(1, 19) },
  { label: "En 3 días, 11hs", valor: () => atajo(3, 11) },
];

const SIN_GRUPOS = "Elegí al menos un grupo para programar";

export function ProgramarEnvio({ campaignId, grupos, sinGrupos, lineas, onCambio, programadoPara, programacionVencida }: Props) {
  const [abierto, setAbierto] = useState(false);
  const [cuando, setCuando] = useState("");
  const [cargando, setCargando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);

  /**
   * ¿Hay algún WhatsApp conectado? Se consulta al backend en vez de guardarse de la respuesta del
   * POST: si dependiera del POST, el aviso sólo existiría en el instante de programar y se
   * perdería al recargar la página — que es justo cuando hace falta (la campaña queda agendada
   * días, y si a la hora no hay línea no sale nada). Se re-chequea cuando el estado cambia.
   */
  const [faltanLineas, setFaltanLineas] = useState(false);
  useEffect(() => {
    let vivo = true;
    activaApi
      .lines()
      .then((r: any) => { if (vivo) setFaltanLineas(!(r.data || []).some((l: any) => l.conectada)); })
      .catch(() => { /* si no se puede saber, no se inventa una alarma */ });
    return () => { vivo = false; };
  }, [programadoPara]);

  // el input no debe dejar elegir el pasado (el backend igual lo rechaza, pero mejor no ofrecerlo)
  const minimo = paraInput(new Date(Date.now() + 60_000));

  async function programar() {
    // Guarda defensiva además del botón deshabilitado: una lista vacía viaja como "sin grupos" y
    // del otro lado la API la lee como TODOS. Programar para nadie no tiene sentido, y programar
    // para todos cuando no se eligió ninguno sale solo a la hora, sin nadie mirando.
    if (sinGrupos || (grupos && grupos.length === 0)) { setMsg({ ok: false, texto: SIN_GRUPOS }); return; }
    if (!cuando) { setMsg({ ok: false, texto: "Elegí cuándo querés que salga" }); return; }
    setCargando(true); setMsg(null);
    try {
      // datetime-local da hora LOCAL sin zona; el Date la interpreta como local y el ISO la
      // convierte a UTC. Mandar el string crudo haría que el backend lo lea como UTC y la
      // campaña saldría 3 horas corrida.
      const iso = new Date(cuando).toISOString();
      const r: any = await activaApi.schedule(campaignId, iso, grupos, lineas?.length ? lineas : undefined);
      // `req()` NO tira excepción con un 422 (los devuelve para que la UI lea el `code`): sin este
      // chequeo, un rechazo del backend —fecha pasada, mensajes sin generar, techo desactualizado—
      // se mostraba en verde como "Programada." y la campaña NO quedaba programada. El operador se
      // iba tranquilo y no salía nada.
      if (r?.success === false || r?.status >= 400) {
        setMsg({ ok: false, texto: r?.error || "No se pudo programar" });
        return;
      }
      // el backend es la autoridad sobre si falta conectar (lo evalúa con el mismo dry-run del envío)
      if (typeof r.data?.faltanLineas === "boolean") setFaltanLineas(r.data.faltanLineas);
      // sin línea conectada NO es un éxito limpio: se programó, pero así no va a salir
      setMsg({ ok: !r.data?.faltanLineas && r.data?.caeEnFranja !== false, texto: r.data?.mensaje || "Programada." });
      onCambio?.();
    } catch (e: any) {
      setMsg({ ok: false, texto: e?.message || "No se pudo programar" });
    } finally { setCargando(false); }
  }

  async function cancelar() {
    setCargando(true); setMsg(null);
    try {
      const r: any = await activaApi.cancelSchedule(campaignId);
      // mismo motivo que en programar(): un 422 no tira excepción. Decir "cancelada" cuando no se
      // canceló es peor que el error: la campaña sale igual y nadie la está esperando.
      if (r?.success === false || r?.status >= 400) {
        setMsg({ ok: false, texto: r?.error || "No se pudo cancelar" });
        return;
      }
      setMsg({ ok: true, texto: r.data?.mensaje || "Programación cancelada." });
      setAbierto(false); setCuando("");
      onCambio?.();
    } catch (e: any) {
      setMsg({ ok: false, texto: e?.message || "No se pudo cancelar" });
    } finally { setCargando(false); }
  }

  if (programadoPara) {
    const f = new Date(programadoPara);
    // vencida = el scheduler la vio demasiado tarde y NO la mandó: hay que decidir a mano
    const tono = programacionVencida
      ? { borde: "border-amber-300", fondo: "bg-amber-50/60", icono: "text-amber-600", titulo: "text-amber-900", texto: "text-amber-700" }
      : { borde: "border-indigo-200", fondo: "bg-indigo-50/50", icono: "text-indigo-600", titulo: "text-indigo-900", texto: "text-indigo-700" };
    return (
      <div className={`rounded-2xl border ${tono.borde} ${tono.fondo} p-4`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            {programacionVencida
              ? <AlertTriangle size={18} className={`${tono.icono} flex-shrink-0 mt-0.5`} />
              : <CalendarClock size={18} className={`${tono.icono} flex-shrink-0 mt-0.5`} />}
            <div>
              <p className={`text-sm font-semibold ${tono.titulo}`}>
                {programacionVencida ? "No salió a horario" : "Programada"} para el{" "}
                {f.toLocaleString("es-AR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
              </p>
              <p className={`text-xs ${tono.texto} mt-0.5`}>
                {programacionVencida
                  ? "Pasó demasiado tiempo, así que no se mandó sola: un mensaje muy a destiempo confunde más de lo que vende. Mandala vos si todavía tiene sentido."
                  : "Sale sola. No hace falta que dejes esto abierto."}
              </p>
              {faltanLineas && !programacionVencida && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5 mt-2 flex items-start gap-1.5">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                  Todavía no hay ningún WhatsApp conectado: si no conectás uno antes de esa hora, no va a salir.
                </p>
              )}
            </div>
          </div>
          <button onClick={cancelar} disabled={cargando} className={`flex items-center gap-1 rounded-lg border ${tono.borde} bg-white px-2.5 py-1.5 text-xs font-semibold ${tono.texto} hover:opacity-80 disabled:opacity-50 flex-shrink-0`}>
            {cargando ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Cancelar
          </button>
        </div>
        {msg && <p className={`text-xs mt-2 ${msg.ok ? tono.texto : "text-red-600"}`}>{msg.texto}</p>}
      </div>
    );
  }

  if (!abierto) {
    return (
      <button onClick={() => setAbierto(true)} className="w-full flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition">
        <CalendarClock size={15} /> Prefiero programarlo para más tarde
      </button>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><CalendarClock size={15} /> ¿Cuándo querés que salga?</p>
        <button onClick={() => { setAbierto(false); setMsg(null); }} className="text-slate-400 hover:text-slate-600"><X size={15} /></button>
      </div>

      <div className="flex flex-wrap gap-2">
        {ATAJOS.map((a) => (
          <button
            key={a.label}
            onClick={() => setCuando(a.valor())}
            className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${cuando === a.valor() ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            {a.label}
          </button>
        ))}
      </div>

      <input
        type="datetime-local"
        value={cuando}
        min={minimo}
        onChange={(e) => setCuando(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
      />

      <button
        onClick={programar}
        disabled={cargando || !cuando || sinGrupos}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-40 transition"
      >
        {cargando ? <><Loader2 size={14} className="animate-spin" /> Programando…</> : <><CalendarClock size={14} /> Programar el envío</>}
      </button>

      {sinGrupos && !msg && (
        <p className="text-xs rounded-lg px-2.5 py-2 flex items-start gap-1.5 text-amber-700 bg-amber-50">
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /> {SIN_GRUPOS}
        </p>
      )}

      {msg && (
        <p className={`text-xs rounded-lg px-2.5 py-2 flex items-start gap-1.5 ${msg.ok ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"}`}>
          {msg.ok ? <Check size={12} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />}
          {msg.texto}
        </p>
      )}

      <p className="text-[11px] text-slate-400">
        Si el sistema está caído a esa hora, la campaña <span className="font-medium">no se manda tarde por su cuenta</span>: queda marcada para que decidas vos.
      </p>
    </div>
  );
}
